import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { SlotUIActions } from '../types';
import { applyTheme, type ThemeInput } from '../themes';
import { ReelsFrame } from './ReelsFrame';
import { ReelsStack, type ReelsStackHandle } from './ReelsStack';

import { SpinButton } from './SpinButton';
import { AutoButton } from './AutoButton';
import { BetButton } from './BetButton';
import { WinBar } from './WinBar';
import { BalanceBar, NextSlotHint } from './BalanceBar';
import { SwipeHint } from './SwipeHint';
import { FreespinsBadge } from './FreespinsBadge';
import { BetModal, AutospinsModal } from './PickerModal';
import { NotEnoughMoneyModal } from './NotEnoughMoneyModal';
import { Preloader, type PreloaderProps } from './Preloader';
import { InAppBrowserWarning } from './InAppBrowserWarning';
import { isInAppBrowser, hasInAppBrowserParam } from '../webviewDetect';

export interface SlotUIProps extends SlotUIActions {
  // ─── Layout ──────────────────────────────────────────────────
  /** Game title shown in the top bar. */
  title?: string;
  /** Small label under the title, uppercase. */
  subtitle?: string;

  // ─── Reels iframe ────────────────────────────────────────────
  /** URL loaded into the reels iframe. Defaults to http://localhost:3000/#remoteUI. */
  reelsUrl?: string;
  /** iframe sandbox attribute (space-separated tokens). */
  reelsSandbox?: string;
  /** iframe allow attribute (e.g. 'autoplay; fullscreen'). */
  reelsAllow?: string;

  // ─── TikTok mode ─────────────────────────────────────────────
  /**
   * Render the reels area as a swipeable vertical stack (TikTok-style).
   * The first frame loads `reelsUrl`; each time the current game dispatches
   * the `loadedEventName` on document (or via `postMessage`), the next URL
   * from `nextReelsUrlFactory` is appended below. Swipe up to advance.
   * Only active in `fullscreen` mode.
   */
  tiktokMode?: boolean;
  /** Event name that signals a game has finished loading. Default: `'GAME_LOADED'`. */
  loadedEventName?: string;
  /**
   * Produces the URL for each subsequent frame. `index === 0` is the second
   * frame. Default: `http://localhost:${3001 + index}/#remoteUI`.
   */
  nextReelsUrlFactory?: (index: number) => string;

  // ─── Game state ──────────────────────────────────────────────
  bet?: number;
  balance?: number;
  win?: number;
  auto?: number;
  freespins?: number;
  currency?: string;
  /** Options shown in the bet modal. */
  betOptions?: number[];
  /** Options shown in the autospin modal. */
  autoOptions?: number[];
  /**
   * Whether the reels are currently spinning. Drives the SpinButton's
   * Spin → Stop icon flip and its aria-label. When `tiktokMode` is on,
   * consumers typically wire this from `onSpinningChange` so the button
   * mirrors the game's own `emitSpinStart/Stop` signals.
   */
  spinning?: boolean;
  /**
   * Whether the Bet button is currently interactable. When false, the
   * bet modal won't open. Consumers typically mirror the game's
   * `onBetChangeButtonsEnabled` callback here — Playmer disables bet
   * changes during free spins.
   */
  betEnabled?: boolean;
  /**
   * Whether the spin control cluster is interactable. When false: the
   * Auto and Bet buttons are hidden, the Spin button is dimmed and
   * inert, and the whole control row becomes pointer-transparent so taps
   * fall through to the hosted game underneath — used while an in-game
   * bonus round wants direct control of the reel canvas. Consumers
   * typically mirror this from `onSpinButtonEnabledChange`, driven by
   * the game's `game.enableSpinButton` / `game.disableSpinButton`.
   * Default: `true`.
   */
  spinButtonEnabled?: boolean;
  /**
   * Whether turbo mode is enabled. Toggled by long-pressing the spin
   * button; when true, a lightning badge is shown. Consumers should
   * flip this from `onTurboChange` to make the gesture persistent.
   */
  turbo?: boolean;

  // ─── Appearance ──────────────────────────────────────────────
  /**
   * Theme to apply on mount / when it changes. Accepts a preset key
   * ('mint', 'midnight', 'sunset', 'dusk-teal'), a full Theme, or a
   * partial `{ vars }` patch. If omitted, the parent app is expected
   * to apply the theme itself.
   */
  theme?: ThemeInput;

  // ─── Display container ───────────────────────────────────────
  /**
   * Render inside a device frame with fixed dimensions (390×780, rounded
   * corners, shadow) and auto-scale it to fit the viewport. Mirrors the
   * Universal Slot UI prototype.
   * Disable for embedding inside an already-sized container.
   */
  scaleToFit?: boolean;
  /**
   * Fill the parent container edge-to-edge with no device mockup chrome
   * (no rounded corners, no shadow, no letterboxing). Implies
   * `scaleToFit = false`. Use this for production embedding where the
   * parent element controls sizing (e.g. the game canvas).
   */
  fullscreen?: boolean;
  /** Custom menu handler (e.g. open a paytable / rules sheet). */
  onMenuClick?: () => void;

  // ─── Preloader overlay ───────────────────────────────────────
  /**
   * Show a full-cover loading overlay until the first hosted game
   * reports it's finished loading (via the configured `loadedEventName`
   * — same contract Application.loadingComplete() uses). Pass `true`
   * for defaults, or an options object to customize the label, title,
   * fade-out duration, etc. Default: `false`.
   */
  preloader?: boolean | Omit<PreloaderProps, 'loadedEventName'>;

  // ─── In-app browser gate ─────────────────────────────────────
  /**
   * Refuse to render the game (showing `InAppBrowserWarning` instead) when
   * `isInAppBrowser()` detects a messaging/social app's in-app WebView
   * (Telegram, Viber, WhatsApp, Instagram, …) — those commonly break
   * WebGL, autoplay audio, and fullscreen. Set to `false` if you knowingly
   * host SlotUI inside your own app's WebView rather than a third-party
   * in-app browser. Default: `true`.
   */
  blockInAppBrowsers?: boolean;
  /** Title for the in-app-browser warning. See `InAppBrowserWarningProps`. */
  inAppBrowserTitle?: string;
  /** Message for the in-app-browser warning. See `InAppBrowserWarningProps`. */
  inAppBrowserMessage?: string;
}

const PORTRAIT_SIZE = { w: 390, h: 780 } as const;
const DEFAULT_REELS_URL = 'http://localhost:3000/#remoteUI';

// Pull a number out of an unknown payload by key name. Returns null when
// the payload isn't an object or the key doesn't resolve to a finite number.
// Used to decode the small, well-known shapes emitted by Playmer's wrapper
// (`{ value }`, `{ amount }`, `{ betValue }`, …).
function readNumber(payload: unknown, key: string): number | null {
  if (!payload || typeof payload !== 'object') return null;
  const v = (payload as Record<string, unknown>)[key];
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

// Specifically handles the `SimpleValue` shape Playmer uses for balance and
// win updates (`{ value: number }`). Also accepts a bare number for the
// manual-testing case when the game posts `emitBalanceUpdate(42)` by mistake.
function readNumberFromValue(payload: unknown): number | null {
  if (typeof payload === 'number' && Number.isFinite(payload)) return payload;
  return readNumber(payload, 'value');
}

// Pull a nested number at `payload[outer][inner]`. Used for the `game.init`
// payload where bet is `{ bet: { default, steps } }`.
function readNestedNumber(payload: unknown, outer: string, inner: string): number | null {
  if (!payload || typeof payload !== 'object') return null;
  const child = (payload as Record<string, unknown>)[outer];
  return readNumber(child, inner);
}

// Pull a number[] at `payload[key]`. Returns null when missing, not an
// array, or containing non-finite values (the BetModal would reject those).
function readNumberArray(payload: unknown, key: string): number[] | null {
  if (!payload || typeof payload !== 'object') return null;
  const arr = (payload as Record<string, unknown>)[key];
  if (!Array.isArray(arr)) return null;
  const nums = arr.filter((n): n is number => typeof n === 'number' && Number.isFinite(n));
  return nums.length === arr.length ? nums : null;
}

// Nested version of `readNumberArray` for `{ bet: { steps: number[] } }`.
function readNestedNumberArray(payload: unknown, outer: string, inner: string): number[] | null {
  if (!payload || typeof payload !== 'object') return null;
  const child = (payload as Record<string, unknown>)[outer];
  return readNumberArray(child, inner);
}

/**
 * Top-level slot UI shell. Fully controlled — consumers pass state and
 * handle events via the `on*` callbacks. For uncontrolled game state,
 * wrap this in your own state container.
 *
 * The reels region is rendered as an iframe that loads `reelsUrl`
 * (defaults to http://localhost:3000/). The actual game canvas runs
 * inside the frame; this module only owns the chrome.
 */
export function SlotUI({
  title = 'Slot',
  reelsUrl = DEFAULT_REELS_URL,
  reelsSandbox,
  reelsAllow,

  tiktokMode = false,
  loadedEventName,
  nextReelsUrlFactory,

  bet = 100,
  balance = 1000,
  win = 0,
  auto = 0,
  freespins = 0,
  currency = '$',
  betOptions,
  autoOptions,
  spinning = false,
  betEnabled = true,
  spinButtonEnabled = true,
  turbo = false,

  theme,
  scaleToFit = true,
  fullscreen = false,
  onMenuClick,
  onSpin,
  onBetChange,
  onAutoChange,
  onTurboChange,
  onBalanceUpdate,
  onWinUpdate,
  onFreespinsUpdate,
  onBetOptionsUpdate,
  onGameInit,
  onSpinningChange,
  onBetChangeButtonsEnabled,
  onSpinButtonEnabledChange,
  onAutoStateChange,
  onGoToBank,
  preloader = false,

  blockInAppBrowsers = true,
  inAppBrowserTitle,
  inAppBrowserMessage,
}: SlotUIProps) {
  const [betOpen, setBetOpen] = useState(false);
  const [gameLoaded, setGameLoaded] = useState(false);
  const [autoOpen, setAutoOpen] = useState(false);
  // Not-enough-money popup. Raised by the game via `game.showNotEnoughMoney`
  // when a spin is attempted with insufficient balance. The payload (when
  // provided) drives the "Need X · Have Y" line inside the modal.
  const [notEnoughMoney, setNotEnoughMoney] = useState<{
    open: boolean;
    required?: number;
    balance?: number;
    currency?: string;
  }>({ open: false });
  // Status of the next tiktok reel (what the user would swipe to), surfaced
  // in the bottom panel. `null` when tiktokMode is off.
  const [nextLoading, setNextLoading] = useState<{
    status: 'none' | 'loading' | 'ready';
    nextIndex: number;
    progress: number;
  } | null>(null);
  // Live drag state from ReelsStack — piped down so the bottom-panel hint
  // can parallax along with the moving reels.
  const [reelDrag, setReelDrag] = useState<{ offset: number; dragging: boolean }>({
    offset: 0,
    dragging: false,
  });
  // document.title of the active tiktok iframe (cross-origin — read via
  // postMessage from the game). Overrides the static `title` prop when present.
  const [activeIframeTitle, setActiveIframeTitle] = useState<string | null>(null);


  // Imperative handle to the reel stack — used to postMessage to the active
  // iframe on button presses (spin/bet/auto). This keeps `App.tsx` free of
  // any knowledge about iframes: the consumer just hands us `onSpin` and we
  // bridge it into the game's external UI protocol (`ui.spinClick`, etc.).
  const reelsStackRef = useRef<ReelsStackHandle>(null);

  const postToActiveGame = useCallback(
    (type: string, payload?: unknown): boolean => {
      const api = reelsStackRef.current;
      if (!api) {
        // tiktokMode off / iframe stack not mounted — no bridge to post through.
        return false;
      }
      api.postToActive(type, payload);
      return true;
    },
    [],
  );

  // Wrap the consumer's `onSpin` so pressing Spin both:
  //   (1) posts `ui.spinClick` to the active reel iframe (if tiktokMode is on),
  //       which the in-game external-wrapper bridge forwards to `onSpinClick`
  //       listeners inside Playmer.ts;
  //   (2) still invokes the app-level `onSpin` prop so demo/local state
  //       (e.g. win count-up) keeps working.
  const handleSpin = useCallback(() => {
    if (tiktokMode) postToActiveGame('ui.spinClick');
    onSpin?.();
  }, [tiktokMode, postToActiveGame, onSpin]);

  // Same bridge for bet changes — game code that listens for `onBetChange`
  // gets the new value via the external-UI bridge. The payload shape has
  // to match what `@playmer/ui-wrapper`'s in-game listener expects, NOT
  // the raw value the consumer hands us:
  //   onBetChange   → `{ originalBetValue: number }` — read by Playmer.ts
  //                   to write `store.bet`. Sending a bare number would
  //                   leave `data.originalBetValue` undefined and silently
  //                   wipe the bet.
  //   onAutoChange  → `AutospinsData` (Playmer reads `spinsCount`). The
  //                   shell's autospin modal only picks a count, so the
  //                   other fields stay undefined — matches what the
  //                   internal <ui-wrapper> emits when only the count
  //                   is changing.
  //   onTurboChange → `{ enabled: boolean }` — Playmer reads `data.enabled`.
  // Keep the consumer-facing `onBetChange?(next)` etc. as raw values
  // because that's what App.tsx state setters want.
  const handleBetChange = useCallback(
    (next: number) => {
      if (tiktokMode) postToActiveGame('ui.betChange', { originalBetValue: next });
      onBetChange?.(next);
    },
    [tiktokMode, postToActiveGame, onBetChange],
  );
  const handleAutoChange = useCallback(
    (next: number) => {
      if (tiktokMode) postToActiveGame('ui.autoChange', { spinsCount: next });
      onAutoChange?.(next);
    },
    [tiktokMode, postToActiveGame, onAutoChange],
  );
  // Long-press on SpinButton → turbo toggle. We mirror the same
  // game-bridge pattern: post `ui.turboChange` into the active reel
  // iframe (the external-UI wrapper exposes it as `onTurboChange` to
  // the game's listeners, with a `{ enabled }` payload) and surface
  // the value to the consumer so their state tracks the new mode.
  const handleTurboToggle = useCallback(
    (next: boolean) => {
      if (tiktokMode) postToActiveGame('ui.turboChange', { enabled: next });
      onTurboChange?.(next);
    },
    [tiktokMode, postToActiveGame, onTurboChange],
  );

  // Stable references for AutoButton/BetButton's onClick/onStop — these
  // used to be inline arrows recreated on every SlotUI render (including
  // every swipe-drag frame), which would defeat React.memo on those
  // buttons even though none of their actual inputs (spinning,
  // spinButtonEnabled, betEnabled) change on those frames.
  const handleOpenBet = useCallback(() => {
    if (!spinning && spinButtonEnabled && betEnabled) setBetOpen(true);
  }, [spinning, spinButtonEnabled, betEnabled]);
  const handleOpenAuto = useCallback(() => {
    if (!spinning && spinButtonEnabled) setAutoOpen(true);
  }, [spinning, spinButtonEnabled]);
  const handleStopAuto = useCallback(() => {
    handleAutoChange(0);
  }, [handleAutoChange]);
  // Same story for the modals — stable onClose/onGoToBank references so
  // React.memo on BetModal/AutospinsModal/NotEnoughMoneyModal actually
  // holds instead of re-rendering (while closed, off-screen) on every
  // unrelated SlotUI render.
  const handleCloseBet = useCallback(() => setBetOpen(false), []);
  const handleCloseAuto = useCallback(() => setAutoOpen(false), []);
  const handleCloseNotEnoughMoney = useCallback(
    () => setNotEnoughMoney((s) => ({ ...s, open: false })),
    [],
  );
  const handleGoToBank = useCallback(() => {
    setNotEnoughMoney((s) => ({ ...s, open: false }));
    onGoToBank?.();
  }, [onGoToBank]);

  // Inbound bridge: game → shell.
  //
  // The game (via its external-UI wrapper in Playmer.ts) posts `game.*`
  // messages whenever its internal state changes — balance after a spin,
  // new bet options on init, win count, remaining free spins, etc.
  // Here we mirror those into the consumer's callback props so the React
  // state in App.tsx tracks the game's truth without the consumer having
  // to know anything about postMessage or iframes.
  //
  // We only accept messages in tiktokMode (non-tiktok embeds use the static
  // `reelsUrl` prop, a single ReelsFrame — no ref handle to filter against),
  // and we filter by `e.source` so that a reel which is off-screen (paused,
  // different game, different balance) can't hijack the visible numbers.
  // Ref-wrap every callback this listener touches — mirrors the pattern
  // ReelsStack uses for its own listeners (onDragChangeRef etc.). Without
  // this, the effect's dependency array below would need to list all ten
  // raw callback props, and `window.addEventListener('message', ...)`
  // would unsubscribe/resubscribe on every render in which a consumer
  // passes a new inline arrow function instead of a stable reference.
  // Reading through a ref means the listener is attached exactly once per
  // `tiktokMode` value and always calls whatever the latest prop is.
  const onBalanceUpdateRef = useRef(onBalanceUpdate);
  onBalanceUpdateRef.current = onBalanceUpdate;
  const onWinUpdateRef = useRef(onWinUpdate);
  onWinUpdateRef.current = onWinUpdate;
  const onBetChangeRef = useRef(onBetChange);
  onBetChangeRef.current = onBetChange;
  const onFreespinsUpdateRef = useRef(onFreespinsUpdate);
  onFreespinsUpdateRef.current = onFreespinsUpdate;
  const onBetOptionsUpdateRef = useRef(onBetOptionsUpdate);
  onBetOptionsUpdateRef.current = onBetOptionsUpdate;
  const onGameInitRef = useRef(onGameInit);
  onGameInitRef.current = onGameInit;
  const onSpinningChangeRef = useRef(onSpinningChange);
  onSpinningChangeRef.current = onSpinningChange;
  const onAutoStateChangeRef = useRef(onAutoStateChange);
  onAutoStateChangeRef.current = onAutoStateChange;
  const onBetChangeButtonsEnabledRef = useRef(onBetChangeButtonsEnabled);
  onBetChangeButtonsEnabledRef.current = onBetChangeButtonsEnabled;
  const onSpinButtonEnabledChangeRef = useRef(onSpinButtonEnabledChange);
  onSpinButtonEnabledChangeRef.current = onSpinButtonEnabledChange;

  useEffect(() => {
    if (!tiktokMode) return;
    const onMessage = (e: MessageEvent) => {
      const d = e.data;
      if (!d || typeof d !== 'object') return;
      const type = (d as { type?: unknown }).type;
      if (typeof type !== 'string' || !type.startsWith('game.')) return;
      // Ignore messages from iframes that aren't currently visible. The
      // ref might briefly be null during reel swaps — in that case
      // be conservative and drop the message.
      if (!reelsStackRef.current?.isActiveSource(e.source)) return;

      const payload = (d as { payload?: unknown }).payload;
      switch (type) {
        case 'game.balanceUpdate': {
          const v = readNumberFromValue(payload);
          if (v !== null) onBalanceUpdateRef.current?.(v);
          break;
        }
        case 'game.winUpdate': {
          const v = readNumberFromValue(payload);
          if (v !== null) onWinUpdateRef.current?.(v);
          break;
        }
        case 'game.betValueAndStepsUpdate': {
          // Playmer emits `{ betValue, steps }`. Surface both — the bet
          // value drives the displayed number, `steps` feeds the BetModal.
          const v = readNumber(payload, 'betValue');
          if (v !== null) onBetChangeRef.current?.(v);
          const steps = readNumberArray(payload, 'steps');
          if (steps) onBetOptionsUpdateRef.current?.(steps);
          break;
        }
        case 'game.freeSpinsNumberUpdate': {
          // Playmer emits `{ amount: number }`.
          const v = readNumber(payload, 'amount');
          if (v !== null) onFreespinsUpdateRef.current?.(v);
          break;
        }
        case 'game.spinStart': {
          // Reels started spinning (emitted by `wrapper.emitSpinStart`).
          // Lock the button into its Stop state until spinStop arrives.
          onSpinningChangeRef.current?.(true);
          break;
        }
        case 'game.spinStop': {
          // Reels came to rest — flip the SpinButton back to Spin.
          onSpinningChangeRef.current?.(false);
          break;
        }
        case 'game.autoStart': {
          // `{ amount: number }` — remaining spins in the auto session.
          const v = readNumber(payload, 'amount');
          if (v !== null) onAutoStateChangeRef.current?.(v);
          break;
        }
        case 'game.autoStop': {
          // No payload — game aborted or finished the auto session.
          onAutoStateChangeRef.current?.(0);
          break;
        }
        case 'game.enableBetChangeButtons': {
          onBetChangeButtonsEnabledRef.current?.(true);
          break;
        }
        case 'game.disableBetChangeButtons': {
          onBetChangeButtonsEnabledRef.current?.(false);
          break;
        }
        case 'game.enableSpinButton': {
          onSpinButtonEnabledChangeRef.current?.(true);
          break;
        }
        case 'game.disableSpinButton': {
          // Game wants exclusive control of the reel canvas (e.g. a bonus
          // pick round) — hide Auto/Bet and let taps pass through.
          onSpinButtonEnabledChangeRef.current?.(false);
          break;
        }
        case 'game.showNotEnoughMoney': {
          // Game-side balance check failed — surface the popup with the
          // shortfall details. Payload is `{ required, balance, currency }`
          // (see Playmer.NotEnoughMoneyPayload); all fields optional, the
          // modal degrades gracefully with just a generic message.
          const required = readNumber(payload, 'required') ?? undefined;
          const balanceFromPayload =
            readNumber(payload, 'balance') ?? undefined;
          const cur =
            payload && typeof payload === 'object'
              ? (payload as { currency?: unknown }).currency
              : undefined;
          setNotEnoughMoney({
            open: true,
            required,
            balance: balanceFromPayload,
            currency: typeof cur === 'string' ? cur : undefined,
          });
          break;
        }
        case 'game.init': {
          // First-load config from the game. Seed every piece of state the
          // shell displays so the UI matches the game's reality before the
          // player does anything. Shape (from Playmer's WrapperOptions):
          //   { balance, bet: { default, steps }, win, title, ... }
          // Fields not emitted (e.g. title handled by game.title elsewhere,
          // features/locale opaque to the shell) are skipped.
          onGameInitRef.current?.(payload);
          const balance = readNumber(payload, 'balance');
          if (balance !== null) onBalanceUpdateRef.current?.(balance);
          const win = readNumber(payload, 'win');
          if (win !== null) onWinUpdateRef.current?.(win);
          const betDefault = readNestedNumber(payload, 'bet', 'default');
          if (betDefault !== null) onBetChangeRef.current?.(betDefault);
          const betSteps = readNestedNumberArray(payload, 'bet', 'steps');
          if (betSteps) onBetOptionsUpdateRef.current?.(betSteps);
          break;
        }
        default:
          // No sync needed for non-state messages (e.g. game.title handled
          // inside ReelsStack).
          break;
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [tiktokMode]);

  // Pause the active reel's render loop while any modal is open. Modals
  // sit on top of the iframe behind a scrim — the player can't see the
  // game anyway, and animating it wastes GPU plus jitters the modal
  // sheet's transform-driven slide-in on lower-end devices.
  //
  // We thread this into ReelsStack via the `paused` prop rather than
  // posting our own pause message: ReelsStack already owns the
  // pause/resume cadence (swipe, drag, iframe load) and would otherwise
  // un-pause the active iframe whenever its own deps changed (e.g. on a
  // preload appending to `urls`). Letting it own the flag keeps the
  // pause behaviour correct even mid-modal.
  const anyModalOpen = betOpen || autoOpen || notEnoughMoney.open;

  // Autoplay engine. `auto` is just a remaining-rounds counter and
  // `spinning` just reflects whether the reels are currently in motion —
  // neither one does anything by itself. This effect is what actually
  // turns them into autoplay:
  //   • Arming a fresh session (auto goes 0 → N via the autospins picker)
  //     fires the first spin immediately, so choosing a count starts
  //     spinning right away instead of waiting for a manual press.
  //   • The instant a spin actually starts (spinning false → true, i.e.
  //     the game's `emitSpinStart`/`game.spinStart` arrived) the remaining
  //     count drops by one — the round is now "in flight", so the badge
  //     shows what's left *after* this one rather than waiting for it to
  //     finish.
  //   • When that spin comes to rest (spinning true → false /
  //     `emitSpinStop`) and rounds are still left, fire the next one right
  //     away — no artificial pacing delay.
  //   • Hits 0 → the session ends on its own; `onStopAuto` (which just
  //     sets the count to 0) short-circuits it early the same way.
  const prevSpinningForAutoRef = useRef(spinning);
  const prevAutoRef = useRef(auto);
  useEffect(() => {
    const wasSpinning = prevSpinningForAutoRef.current;
    const wasAuto = prevAutoRef.current;
    prevSpinningForAutoRef.current = spinning;
    prevAutoRef.current = auto;

    // A modal (bet/auto picker, insufficient-funds popup) is already
    // pausing the reel — don't drive spins underneath it. Same if the
    // game has taken exclusive control of the canvas (disableSpinButton,
    // e.g. a bonus round) — the shell shouldn't keep clicking spin from
    // underneath that.
    if (anyModalOpen || !spinButtonEnabled) return;

    // Arm a fresh session while idle: kick off the first spin. The count
    // itself isn't touched here — it's decremented below, keyed off that
    // spin actually starting.
    if (wasAuto === 0 && auto > 0 && !spinning) {
      handleSpin();
      return;
    }

    // A spin just started — burn one round immediately.
    if (!wasSpinning && spinning && auto > 0) {
      handleAutoChange(auto - 1);
      return;
    }

    // A spin just came to rest with rounds still left (already
    // decremented above) — fire the next one.
    if (wasSpinning && !spinning && auto > 0) {
      handleSpin();
    }
  }, [spinning, auto, anyModalOpen, spinButtonEnabled, handleSpin, handleAutoChange]);

  // Insufficient funds should cancel an in-progress autoplay session
  // outright rather than silently stalling it — the player needs to
  // top up before spins can continue, so there is nothing to resume.
  useEffect(() => {
    if (notEnoughMoney.open && auto > 0) handleAutoChange(0);
  }, [notEnoughMoney.open, auto, handleAutoChange]);

  // Fullscreen implies no device mockup + no scaling.
  const effectiveScaleToFit = fullscreen ? false : scaleToFit;

  useEffect(() => {
    if (theme !== undefined) applyTheme(theme);
  }, [theme]);

  // Fit-to-viewport scaling — optional.
  const stageRef = useRef<HTMLDivElement | null>(null);
  const [scale, setScale] = useState(1);
  useLayoutEffect(() => {
    if (!effectiveScaleToFit) {
      setScale(1);
      return;
    }
    const base = PORTRAIT_SIZE;
    const fit = () => {
      const el = stageRef.current;
      if (!el) return;
      const pad = 24;
      const vw = el.clientWidth - pad;
      const vh = el.clientHeight - pad;
      const s = Math.min(vw / base.w, vh / base.h, 1.2);
      setScale(Math.max(0.2, s));
    };
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, [effectiveScaleToFit]);

  // When the TikTok host has picked up the active iframe's document.title,
  // let it override the prop — the reel that's visible should name itself.
  const effectiveTitle =
    tiktokMode && activeIframeTitle && activeIframeTitle.trim().length > 0
      ? activeIframeTitle
      : title;

  const screen = (
    <PortraitScreen
      title={effectiveTitle}
      reelsUrl={reelsUrl}
      reelsSandbox={reelsSandbox}
      reelsAllow={reelsAllow}
      fullscreen={fullscreen}
      tiktokMode={tiktokMode}
      loadedEventName={loadedEventName}
      nextReelsUrlFactory={nextReelsUrlFactory}
      reelsStackRef={reelsStackRef}
      bet={bet}
      balance={balance}
      win={win}
      auto={auto}
      freespins={freespins}
      currency={currency}
      spinning={spinning}
      betEnabled={betEnabled}
      spinButtonEnabled={spinButtonEnabled}
      turbo={turbo}
      reelsPaused={anyModalOpen}
      nextLoading={nextLoading}
      onNextLoadingChange={setNextLoading}
      reelDrag={reelDrag}
      onReelDragChange={setReelDrag}
      onActiveIframeTitleChange={setActiveIframeTitle}
      onMenuClick={onMenuClick}
      onSpin={handleSpin}
      onTurboToggle={handleTurboToggle}
      onOpenBet={handleOpenBet}
      onOpenAuto={handleOpenAuto}
      onStopAuto={handleStopAuto}
    />
  );

  const modals = (
    <>
      <BetModal
        open={betOpen}
        value={bet}
        options={betOptions}
        currency={currency}
        onChoose={handleBetChange}
        onClose={handleCloseBet}
      />
      <AutospinsModal
        open={autoOpen}
        value={auto}
        options={autoOptions}
        onChoose={handleAutoChange}
        onClose={handleCloseAuto}
      />
      <NotEnoughMoneyModal
        open={notEnoughMoney.open}
        required={notEnoughMoney.required}
        balance={notEnoughMoney.balance}
        currency={notEnoughMoney.currency ?? currency}
        onClose={handleCloseNotEnoughMoney}
        // Bank CTA is only rendered when a handler is wired — keeps the
        // popup useful in demo/local mode where there's nowhere to deposit.
        onGoToBank={onGoToBank ? handleGoToBank : undefined}
      />
    </>
  );

  // Preloader overlay — listens for the same `loadedEventName` the
  // ReelsStack uses, so a single signal from the first hosted game both
  // hides the preloader and (in tiktokMode) queues the next reel.
  const preloaderOptions = typeof preloader === 'object' ? preloader : {};
  const preloaderFadeMs = preloaderOptions.fadeMs ?? 400;

  const preloaderNode = preloader
    ? (
      <Preloader
        loadedEventName={loadedEventName}
        {...preloaderOptions}
        onGameLoaded={() => {
          setGameLoaded(true);
          preloaderOptions.onGameLoaded?.();
        }}
      />
    )
    : null;

  // Small nudge: game starts 60px below its natural position and slides into
  // place when the game loads. Keeping the offset small (vs 100%) ensures the
  // browser still paints the iframe — fully off-screen iframes are skipped by
  // the compositor and arrive blank.
  const screenNode = preloader
    ? (
      <div
        style={{
          position: 'absolute',
          inset: 0,
          transform: gameLoaded ? 'translateY(0)' : 'translateY(260px)',
          transition: gameLoaded ? `transform ${preloaderFadeMs}ms cubic-bezier(0.4, 0, 0.2, 1)` : 'none',
        }}
      >
        {screen}
      </div>
    )
    : screen;

  // Refuse to boot the game at all inside a known in-app browser — see
  // `blockInAppBrowsers` prop doc. `hasInAppBrowserParam` covers the cases
  // `isInAppBrowser`'s UA sniffing structurally can't (Telegram/Viber on
  // iOS — see webviewDetect.ts) via a link-level `?inAppBrowser` tag.
  // Placed after every hook above (rather than as an early return further
  // up) so this can never change the hook call order between renders
  // regardless of when `blockInAppBrowsers` flips. Not memoized:
  // `navigator.userAgent`/`location.search` don't change within a page's
  // lifetime, so re-running the checks per render is cheap and avoids a
  // stale result if this component somehow renders before the prop is known.
  if (blockInAppBrowsers && (isInAppBrowser() || hasInAppBrowserParam())) {
    return (
      <InAppBrowserWarning title={inAppBrowserTitle} message={inAppBrowserMessage} />
    );
  }

  // Fullscreen: no device mockup, no letterboxing — fill the parent edge-to-edge.
  if (fullscreen) {
    return (
      <div
        className="reelay-slot fullscreen"
        style={{
          width: '100%',
          height: '100%',
          position: 'relative',
          overflow: 'hidden',
          background: 'var(--bg)',
        }}
      >
        {screenNode}
        {modals}
        {preloaderNode}
      </div>
    );
  }

  const deviceAndModals = (
    <div className="device" style={{ position: 'relative' }}>
      {screenNode}
      {modals}
      {preloaderNode}
    </div>
  );

  if (!effectiveScaleToFit) {
    return <div className="reelay-slot">{deviceAndModals}</div>;
  }

  return (
    <div
      className="reelay-slot"
      ref={stageRef}
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          transformOrigin: 'center center',
          transform: `scale(${scale})`,
          flexShrink: 0,
        }}
      >
        {deviceAndModals}
      </div>
    </div>
  );
}

// ─── Layout sub-screens (not exported — purely composition helpers) ──

interface ScreenProps {
  title: string;
  reelsUrl: string;
  reelsSandbox?: string;
  reelsAllow?: string;
  fullscreen?: boolean;
  tiktokMode?: boolean;
  loadedEventName?: string;
  nextReelsUrlFactory?: (index: number) => string;
  reelsStackRef: React.RefObject<ReelsStackHandle>;
  bet: number;
  balance: number;
  win: number;
  auto: number;
  freespins: number;
  currency: string;
  spinning: boolean;
  betEnabled: boolean;
  /**
   * False while the hosted game has exclusive control of the reel canvas
   * (`game.disableSpinButton`). Hides Auto/Bet, dims Spin, and lets taps
   * fall through to the game underneath.
   */
  spinButtonEnabled: boolean;
  turbo: boolean;
  /**
   * True while any host modal (bet picker, autospin picker, not-enough-
   * money popup) is open. Forwarded to ReelsStack so the active iframe
   * keeps its render loop paused for the duration.
   */
  reelsPaused: boolean;
  nextLoading: { status: 'none' | 'loading' | 'ready'; nextIndex: number; progress: number } | null;
  onNextLoadingChange: (info: { status: 'none' | 'loading' | 'ready'; nextIndex: number; progress: number }) => void;
  reelDrag: { offset: number; dragging: boolean };
  onReelDragChange: (info: { offset: number; dragging: boolean }) => void;
  onActiveIframeTitleChange: (title: string | null) => void;
  onMenuClick?: () => void;
  onSpin?: () => void;
  onTurboToggle?: (next: boolean) => void;
  onOpenBet: () => void;
  onOpenAuto: () => void;
  onStopAuto: () => void;
}

function PortraitScreen({
  title,
  reelsUrl,
  reelsSandbox,
  reelsAllow,
  fullscreen,
  tiktokMode,
  loadedEventName,
  nextReelsUrlFactory,
  reelsStackRef,
  bet,
  balance,
  win,
  auto,
  freespins,
  currency,
  spinning,
  betEnabled,
  spinButtonEnabled,
  turbo,
  reelsPaused,
  nextLoading,
  onNextLoadingChange,
  reelDrag,
  onReelDragChange,
  onActiveIframeTitleChange,
  onMenuClick,
  onSpin,
  onTurboToggle,
  onOpenBet,
  onOpenAuto,
  onStopAuto,
}: ScreenProps) {
  // First-time "swipe up" onboarding hint. Triggers exactly once per
  // session, the very first time the next reel reports 'ready' — which,
  // since ReelsStack only ever queues one reel ahead, can only be true the
  // first time at all while the player is still on the very first game
  // (no separate index check needed). Gated on the exact same conditions
  // that disable the swipe gesture itself (see `swipeDisabled` below) —
  // there's no point teaching a gesture that won't actually do anything
  // right now (mid-spin, mid-autoplay, or with a modal open).
  const swipeBlocked = spinning || !spinButtonEnabled || reelsPaused || auto > 0;
  const [swipeHintPhase, setSwipeHintPhase] = useState<'hidden' | 'visible' | 'fading'>('hidden');
  const swipeHintShownRef = useRef(false);
  const swipeHintTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  useEffect(() => {
    if (swipeHintShownRef.current) return;
    if (nextLoading?.status !== 'ready') return;
    if (swipeBlocked) return;
    swipeHintShownRef.current = true;
    setSwipeHintPhase('visible');
    // 3 full cycles of the 1.6s hand/reel-nudge loop (see `reelay-swipeNudge`
    // and `reelay-swipeHintHand`) = 4800ms. Previously this fired at a flat
    // 3000ms, which lands mid-cycle (1.875 loops in) — the reel-nudge
    // animation would get yanked off (`animationName: 'none'`) mid-motion,
    // a visible jump since it wasn't back at rest yet. Waiting for a whole
    // number of cycles means both animations are already back at rest when
    // the fade-out starts, so nothing gets cut off mid-move.
    swipeHintTimersRef.current.push(
      setTimeout(() => setSwipeHintPhase('fading'), 4800),
      setTimeout(() => setSwipeHintPhase('hidden'), 5200),
    );
  }, [nextLoading?.status, swipeBlocked]);

  // Early-dismiss the instant the player actually starts swiping — no
  // reason to keep teaching a gesture they're already performing — and
  // also the instant swiping itself becomes blocked (a modal opens,
  // autoplay kicks in, etc.), so the hint never lingers over a gesture
  // that would no longer do anything.
  useEffect(() => {
    if (!reelDrag.dragging && !swipeBlocked) return;
    if (swipeHintPhase === 'hidden') return;
    swipeHintTimersRef.current.forEach(clearTimeout);
    swipeHintTimersRef.current = [];
    setSwipeHintPhase('fading');
    swipeHintTimersRef.current.push(setTimeout(() => setSwipeHintPhase('hidden'), 400));
  }, [reelDrag.dragging, swipeBlocked, swipeHintPhase]);

  useEffect(() => () => swipeHintTimersRef.current.forEach(clearTimeout), []);

  return (
    <div
      className="screen portrait"
      style={{
        width: '100%',
        height: '100%',
        background: 'var(--bg)',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
        color: 'var(--text)',
      }}
    >
      {fullscreen && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 0,
            pointerEvents: 'auto',
            // While the swipe hint is showing, nudge the whole reel up and
            // let it spring back — as if the player had actually done the
            // swipe and released it — so the hand icon isn't the only thing
            // selling the gesture. This is a plain CSS animation on an
            // ancestor of ReelsStack, entirely separate from the real
            // drag transform ReelsStack applies internally to its own
            // inner layer, so it can't interfere with (or be interfered
            // with by) actual dragging. Duration matches `reelay-
            // swipeHintHand` (SwipeHint's own hand-icon loop) exactly, and
            // both start on the same `swipeHintPhase` flip, so the reel's
            // rise/release stays locked in step with the hand's. Fixed at
            // 3 iterations (not `infinite`) — the hint's visible window is
            // timed to exactly 3 * 1.6s (see the timers above) specifically
            // so this always finishes its last cycle and is back at rest
            // before `swipeHintPhase` flips away and yanks the animation
            // off; `infinite` would get cut off mid-motion instead.
            animationName: swipeHintPhase === 'visible' ? 'reelay-swipeNudge' : 'none',
            animationDuration: '1.6s',
            animationTimingFunction: 'ease-in-out',
            animationIterationCount: 3,
            animationFillMode: 'forwards',
          }}
        >
          {tiktokMode ? (
            <ReelsStack
              ref={reelsStackRef}
              initialUrl={reelsUrl}
              nextUrlFactory={nextReelsUrlFactory}
              loadedEventName={loadedEventName}
              sandbox={reelsSandbox}
              allow={reelsAllow}
              paused={reelsPaused}
              // `swipeDisabled` locks the whole gesture (including
              // swipe-down-for-history) — kept narrow, matching the
              // original behavior: only while actually spinning or while
              // the game has taken control of input.
              swipeDisabled={spinning || !spinButtonEnabled}
              // `nextDisabled` blocks *only* the swipe-up-to-next-game
              // outcome — while autoplay is queued up (even between
              // individual auto-spins) or any modal is open (bet picker,
              // autospin picker, insufficient-funds popup, or anything else
              // that sets `anyModalOpen` in the parent). History still
              // opens normally in these states; see `swipeBlocked` above.
              nextDisabled={swipeBlocked}
              interactive={!spinButtonEnabled}
              onNextLoadingChange={onNextLoadingChange}
              onDragChange={onReelDragChange}
              onActiveTitleChange={onActiveIframeTitleChange}
            />
          ) : (
            <ReelsFrame
              src={reelsUrl}
              sandbox={reelsSandbox}
              allow={reelsAllow}
              fullscreen
            />
          )}
        </div>
      )}

      {swipeHintPhase !== 'hidden' && (
        <SwipeHint fading={swipeHintPhase === 'fading'} />
      )}

      {/* Everything below is shell chrome painted on top of the reel
          background (z-index 0 above). While the game has taken
          exclusive control of the canvas (`spinButtonEnabled === false`,
          i.e. `game.disableSpinButton`), this whole layer goes
          pointer-transparent so taps reach the iframe underneath —
          otherwise the top bar / win row / balance bar would each keep
          swallowing clicks across their own full-width band even with
          Auto/Bet hidden and Spin disabled. */}
      <div
        style={{
          position: 'relative',
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          pointerEvents: spinButtonEnabled ? 'auto' : 'none',
        }}
      >
      {/* Top bar: title only */}
      <div
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: 28,
          padding: '0 24px',
          background: 'color-mix(in srgb, var(--surface) 50%, transparent)',
          flexShrink: 0,
          position: 'relative',
          zIndex: 3,
          boxSizing: 'border-box',
        }}
      >
        <span style={{ fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,0.7)', letterSpacing: '0.04em' }}>
          {title}
        </span>
      </div>

      {!fullscreen && (
        <div
          style={{
            flex: '1 1 auto',
            padding: '16px 14px 8px',
            display: 'flex',
            minHeight: 0,
            position: 'relative',
            zIndex: 1,
          }}
        >
          <ReelsFrame
            src={reelsUrl}
            sandbox={reelsSandbox}
            allow={reelsAllow}
          />
        </div>
      )}
      {fullscreen && <div style={{ flex: '1 1 auto' }} />}

      {/* Win label sits above the spin button with a small gap */}
      <div
        style={{
          padding: '8px 20px 14px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 12,
          position: 'relative',
          zIndex: 2,
        }}
      >
        <div style={{ flex: '0 1 auto', minWidth: 0 }}>
          <WinBar win={win} currency={currency} />
        </div>
        {freespins > 0 && (
          <div style={{ flex: '0 0 auto' }}>
            <FreespinsBadge count={freespins} size="sm" />
          </div>
        )}
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 22,
          padding: '0 20px 56px',
          position: 'relative',
          zIndex: 1,
        }}
      >
        {spinButtonEnabled && (
          <AutoButton
            count={auto}
            onClick={onOpenAuto}
            onStop={onStopAuto}
            size={52}
            innerSize={28}
            disabled={spinning}
          />
        )}
        <SpinButton
          size={92}
          onClick={onSpin}
          spinning={spinning}
          turbo={turbo}
          onTurboToggle={onTurboToggle}
          disabled={!spinButtonEnabled}
        />
        {spinButtonEnabled && (
          <BetButton
            onClick={onOpenBet}
            size={52}
            danger={bet > balance}
            disabled={!betEnabled || spinning}
          />
        )}
      </div>

      <BalanceBar
        balance={balance}
        bet={bet}
        currency={currency}
      />

      {/* Bottom hint bar — full-width black strip with loading/swipe label */}
      {tiktokMode && (
        <div
          style={{
            width: '100%',
            height: 28,
            background: 'color-mix(in srgb, var(--surface) 70%, transparent)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            position: 'relative',
            zIndex: 3,
          }}
        >
          {/* While the first-time swipe-hint overlay is still showing (or
              fading out), it's already teaching "swipe up for the next
              game" front and center — this bottom-bar label saying the
              exact same thing at the same time reads as an odd double-up,
              so it stays hidden until the overlay fully finishes (`hidden`)
              and only then takes over as the ongoing, subtler reminder. */}
          {nextLoading && nextLoading.status !== 'none'
            && !(nextLoading.status === 'ready' && swipeHintPhase !== 'hidden') && (
            <div style={{
              opacity: spinning ? 0 : 1,
              transform: spinning ? 'translateY(4px)' : 'translateY(0)',
              transition: spinning
                ? 'none'
                : 'opacity 0.35s ease, transform 0.35s cubic-bezier(0.22, 1, 0.36, 1)',
              pointerEvents: spinning ? 'none' : 'auto',
            }}>
              <NextSlotHint
                status={nextLoading.status as 'loading' | 'ready'}
                progress={nextLoading.progress}
                drag={reelDrag}
              />
            </div>
          )}
        </div>
      )}
      </div>
    </div>
  );
}

